import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Snackbar,
} from '@mui/material';
import { Refresh, CheckCircle, Warning, Error as ErrorIcon } from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { useApiClient } from '../utils/apiClient';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const HealthMonitor = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentHealth, setCurrentHealth] = useState(null);
  const [healthHistory, setHealthHistory] = useState([]);
  const [endpointBreakdown, setEndpointBreakdown] = useState(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [nextUpdateTime, setNextUpdateTime] = useState(null);
  const apiClient = useApiClient();
  const isInitialMount = useRef(true);
  const timeRangeRef = useRef(timeRange);
  const cacheRef = useRef(new Map());
  
  // Constants for polling and caching
  const CACHE_TTL = 10 * 60 * 1000; // 10 minutes - matches server cache
  const POLL_INTERVAL = 10 * 60 * 1000; // 10 minutes for automatic updates

  const fetchHealthData = useCallback(async () => {
    console.log('Fetching health data for timeRange:', timeRange);
    const fetchStart = Date.now();
    
    try {
      setRefreshing(true);
      setError(null);

      // Calculate startDate based on timeRange
      const now = new Date();
      let startDate = null;
      
      switch (timeRange) {
        case '15m':
          startDate = new Date(now.getTime() - 15 * 60 * 1000);
          break;
        case '1h':
          startDate = new Date(now.getTime() - 60 * 60 * 1000);
          break;
        case '4h':
          startDate = new Date(now.getTime() - 4 * 60 * 60 * 1000);
          break;
        case '8h':
          startDate = new Date(now.getTime() - 8 * 60 * 60 * 1000);
          break;
        case '24h':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      }

      // Generate cache key
      const cacheKey = `health-${timeRange}-${startDate.getTime()}`;
      
      // Check client-side cache first
      const cached = cacheRef.current.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        console.log('✓ Using cached health data (client-side cache)');
        setCurrentHealth(cached.data.current);
        setHealthHistory(cached.data.history);
        setEndpointBreakdown(cached.data.breakdown);
        setLastUpdateTime(new Date(cached.timestamp));
        setNextUpdateTime(new Date(cached.timestamp + CACHE_TTL));
        setLoading(false);
        setRefreshing(false);
        return;
      }

      const summaryParams = startDate ? `?startDate=${startDate.toISOString()}` : '';

      // Fetch all data in parallel for better performance
      console.log(`📊 Fetching fresh health data with timeRange: ${timeRange}`);
      const [healthResponse, historyResponse, summaryResponse] = await Promise.all([
        apiClient.get('/health'),
        apiClient.get(`/health/history?timeRange=${timeRange}`),
        apiClient.get(`/analytics/summary${summaryParams}`),
      ]);
      
      // Cache the response
      const cacheData = {
        current: healthResponse.data,
        history: historyResponse.data.dataPoints || [],
        breakdown: summaryResponse.data.summary.endpointBreakdown || {},
        timestamp: Date.now(),
      };
      cacheRef.current.set(cacheKey, cacheData);
      
      console.log(`📊 Health history response received with ${historyResponse.data.dataPoints?.length} data points`);

      setCurrentHealth(healthResponse.data);
      setHealthHistory(historyResponse.data.dataPoints || []);
      setEndpointBreakdown(summaryResponse.data.summary.endpointBreakdown || {});
      setLastUpdateTime(new Date());
      setNextUpdateTime(new Date(Date.now() + CACHE_TTL));
      
      const fetchEnd = Date.now();
      console.log(`✅ Health data fetch completed in ${fetchEnd - fetchStart}ms`);
    } catch (err) {
      console.error('Failed to fetch health data:', err);
      setCurrentHealth((prevHealth) => {
        if (!prevHealth) {
          setError('Failed to load health data. Please try again.');
        }
        return prevHealth;
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [timeRange, apiClient, CACHE_TTL]);

  // Update timeRangeRef when timeRange changes
  useEffect(() => {
    timeRangeRef.current = timeRange;
  }, [timeRange]);

  // Debug: Log when healthHistory changes
  useEffect(() => {
    console.log('🔄 healthHistory changed, length:', healthHistory?.length);
    if (healthHistory && healthHistory.length > 0) {
      console.log('First data point:', healthHistory[0]);
      console.log('Last data point:', healthHistory[healthHistory.length - 1]);
    }
  }, [healthHistory]);

  // Initial load and setup auto-refresh (runs ONCE)
  useEffect(() => {
    console.log('Initial setup effect running');
    fetchHealthData();
    
    // Mark as no longer initial mount after first load
    const timer = setTimeout(() => {
      console.log('Marking as no longer initial mount');
      isInitialMount.current = false;
    }, 1000);
    
    // Auto-refresh every 10 minutes to match server cache TTL and Analytics polling
    const interval = setInterval(() => {
      console.log('⏰ Automatic 10-minute refresh triggered');
      fetchHealthData();
    }, POLL_INTERVAL);
    
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, []); // Remove fetchHealthData dependency to avoid circular dependency

  // Fetch when time range changes (after initial mount)
  useEffect(() => {
    console.log('Time range effect - isInitialMount:', isInitialMount.current, 'timeRange:', timeRange);
    if (!isInitialMount.current) {
      console.log('Fetching data for new time range');
      fetchHealthData();
    }
  }, [timeRange]); // Remove fetchHealthData dependency to avoid circular dependency

  const handleCloseError = () => {
    setError(null);
  };

  // Calculate metrics from history data for the selected time range
  const calculateTimeRangeMetrics = () => {
    if (!healthHistory || healthHistory.length === 0) {
      return {
        successRate: 0,
        averageResponseTime: 0,
        totalRequests: 0,
      };
    }

    const totalRequests = healthHistory.reduce((sum, point) => sum + point.requestCount, 0);
    const successfulRequests = healthHistory.reduce((sum, point) => sum + point.successCount, 0);
    const totalResponseTime = healthHistory.reduce((sum, point) => sum + (point.averageResponseTime * point.requestCount), 0);

    return {
      successRate: totalRequests > 0 ? (successfulRequests / totalRequests) * 100 : 0,
      averageResponseTime: totalRequests > 0 ? Math.round(totalResponseTime / totalRequests) : 0,
      totalRequests,
    };
  };

  const timeRangeMetrics = calculateTimeRangeMetrics();

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'degraded':
        return 'warning';
      case 'unhealthy':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle color="success" />;
      case 'degraded':
        return <Warning color="warning" />;
      case 'unhealthy':
        return <ErrorIcon color="error" />;
      default:
        return null;
    }
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Prepare chart data
  const chartData = {
    labels: healthHistory.map(point => {
      const date = new Date(point.timestamp);
      // Always show both date and time for all ranges, with appropriate formatting
      if (timeRange === '30d') {
        // For 30d, show date and time (compact format)
        return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else if (timeRange === '7d') {
        // For 7d, show date and time
        return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else if (timeRange === '24h') {
        // For 24h, show date and time
        return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else if (timeRange === '8h' || timeRange === '4h') {
        // For 4h and 8h, show date and time
        return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else if (timeRange === '1h') {
        // For 1h, show date and time with seconds
        return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
      } else {
        // For 15m, show date and time with seconds
        return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
      }
    }),
    datasets: [
      {
        label: 'Request Count',
        data: healthHistory.map(point => point.requestCount),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.1)',
        yAxisID: 'y',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Success Count',
        data: healthHistory.map(point => point.successCount),
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.1)',
        yAxisID: 'y',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Error Count',
        data: healthHistory.map(point => point.errorCount),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        yAxisID: 'y',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Avg Response Time (ms)',
        data: healthHistory.map(point => point.averageResponseTime),
        borderColor: 'rgb(255, 159, 64)',
        backgroundColor: 'rgba(255, 159, 64, 0.1)',
        yAxisID: 'y1',
        fill: false,
        tension: 0.4,
        borderDash: [5, 5],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `Health & Request Metrics Over Time (${
          timeRange === '15m' ? 'Last 15 Minutes' :
          timeRange === '1h' ? 'Last Hour' :
          timeRange === '4h' ? 'Last 4 Hours' :
          timeRange === '8h' ? 'Last 8 Hours' :
          timeRange === '24h' ? 'Last 24 Hours' :
          timeRange === '7d' ? 'Last 7 Days' :
          timeRange === '30d' ? 'Last 30 Days' : 'Last 24 Hours'
        })`,
        font: {
          size: 16,
        },
      },
      tooltip: {
        callbacks: {
          afterBody: (context) => {
            const dataIndex = context[0].dataIndex;
            const point = healthHistory[dataIndex];
            if (point) {
              const errorRate = point.requestCount > 0 
                ? ((point.errorCount / point.requestCount) * 100).toFixed(2)
                : 0;
              return `Status: ${point.status}\nError Rate: ${errorRate}%`;
            }
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Date & Time',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Request Count',
        },
        beginAtZero: true,
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Response Time (ms)',
        },
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  if (loading && !currentHealth) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Health Monitor</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              label="Time Range"
            >
              <MenuItem value="15m">Last 15 Minutes</MenuItem>
              <MenuItem value="1h">Last Hour</MenuItem>
              <MenuItem value="4h">Last 4 Hours</MenuItem>
              <MenuItem value="8h">Last 8 Hours</MenuItem>
              <MenuItem value="24h">Last 24 Hours</MenuItem>
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
            </Select>
          </FormControl>
          
          {/* Last Update Info */}
          {lastUpdateTime && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'flex-end',
              fontSize: '0.85rem',
              color: 'text.secondary',
              minWidth: 200
            }}>
              <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                Last updated: {lastUpdateTime.toLocaleTimeString()}
              </Typography>
              {nextUpdateTime && (
                <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                  Next update: {nextUpdateTime.toLocaleTimeString()}
                </Typography>
              )}
            </Box>
          )}
          
          <Tooltip title={refreshing ? 'Refreshing...' : 'Manually refresh data now'}>
            <span>
              <IconButton 
                onClick={fetchHealthData} 
                disabled={refreshing}
                sx={{ 
                  animation: refreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  }
                }}
              >
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Current Health Status Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary" variant="body2">
                  System Status
                </Typography>
                {currentHealth && getStatusIcon(currentHealth.status)}
              </Box>
              <Typography variant="h5">
                {currentHealth?.status && (
                  <Chip
                    label={currentHealth.status.toUpperCase()}
                    color={getStatusColor(currentHealth.status)}
                    size="small"
                  />
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Uptime
              </Typography>
              <Typography variant="h5">
                {currentHealth?.uptime ? formatUptime(currentHealth.uptime) : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Success Rate
              </Typography>
              <Typography variant="h5" color={timeRangeMetrics.successRate >= 95 ? 'success.main' : 'warning.main'}>
                {timeRangeMetrics.successRate.toFixed(2)}%
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {timeRange === '15m' && 'Last 15 minutes'}
                {timeRange === '1h' && 'Last hour'}
                {timeRange === '4h' && 'Last 4 hours'}
                {timeRange === '8h' && 'Last 8 hours'}
                {timeRange === '24h' && 'Last 24 hours'}
                {timeRange === '7d' && 'Last 7 days'}
                {timeRange === '30d' && 'Last 30 days'}
              </Typography>
              {endpointBreakdown && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {Object.entries(endpointBreakdown).map(([endpoint, data]) => (
                    <Box key={endpoint} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {endpoint}:
                      </Typography>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                        {data.successRate?.toFixed(1)}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Avg Response Time
              </Typography>
              <Typography variant="h5">
                {timeRangeMetrics.averageResponseTime} ms
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {timeRange === '15m' && 'Last 15 minutes'}
                {timeRange === '1h' && 'Last hour'}
                {timeRange === '4h' && 'Last 4 hours'}
                {timeRange === '8h' && 'Last 8 hours'}
                {timeRange === '24h' && 'Last 24 hours'}
                {timeRange === '7d' && 'Last 7 days'}
                {timeRange === '30d' && 'Last 30 days'}
              </Typography>
              {endpointBreakdown && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {Object.entries(endpointBreakdown).map(([endpoint, data]) => (
                    <Box key={endpoint} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {endpoint}:
                      </Typography>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                        {data.averageResponseTime} ms
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Service Status */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Service Health
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography>Database</Typography>
                  <Chip
                    label={currentHealth?.services?.database ? 'Healthy' : 'Unhealthy'}
                    color={currentHealth?.services?.database ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography>Analytics</Typography>
                  <Chip
                    label={currentHealth?.services?.analytics ? 'Healthy' : 'Unhealthy'}
                    color={currentHealth?.services?.analytics ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Metrics (Last 15m)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Total Requests</Typography>
                  <Typography fontWeight="bold">{currentHealth?.metrics?.totalRequests || 0}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Error Rate</Typography>
                  <Typography fontWeight="bold" color={(currentHealth?.metrics?.errorRate || 0) > 10 ? 'error.main' : 'text.primary'}>
                    {currentHealth?.metrics?.errorRate?.toFixed(2) || 0}%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Combined Chart */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ height: '500px' }}>
          {healthHistory.length > 0 ? (
            <Line data={chartData} options={chartOptions} />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography color="text.secondary">No data available for the selected time range</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Error Snackbar - doesn't cause layout shift */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HealthMonitor;

